import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Loader2, ArrowLeft, CheckCircle, XCircle, FileText, User,
  Building2, DollarSign, MessageSquare, Send, Users, Handshake, Clock
} from "lucide-react";
import { API } from "../data/constants";

const STAGE_META = {
  approved:         { label: "Approved",        text: "text-emerald-700", bg: "bg-emerald-50" },
  capital_matching: { label: "Capital Matching", text: "text-indigo-700",  bg: "bg-indigo-50" },
  terms:            { label: "Terms",           text: "text-violet-700",  bg: "bg-violet-50" },
};
const LOAN_LABELS = { fix_flip: "Fix & Flip", bridge: "Bridge", dscr: "DSCR", cash_out: "Cash-Out Refi", construction: "Construction", ground_up: "Ground Up" };
const NOTE_TYPES = [
  { value: "matching", label: "Matching" },
  { value: "outreach", label: "Outreach" },
  { value: "negotiation", label: "Negotiation" },
  { value: "general", label: "General" },
];
const VALID_TRANSITIONS = {
  approved: ["capital_matching"],
  capital_matching: ["terms", "approved"],
  terms: ["funding", "capital_matching"],
};

const LENDER_STATUS_META = {
  received:       { label: "Received",       color: "bg-gray-100 text-gray-600" },
  reviewing:      { label: "Reviewing",      color: "bg-blue-100 text-blue-700" },
  interested:     { label: "Interested",     color: "bg-emerald-100 text-emerald-700" },
  requested_info: { label: "Info Requested", color: "bg-amber-100 text-amber-700" },
  terms_issued:   { label: "Terms Issued",   color: "bg-violet-100 text-violet-700" },
  selected:       { label: "Selected",       color: "bg-emerald-200 text-emerald-800" },
  passed:         { label: "Passed",         color: "bg-red-100 text-red-600" },
  closed:         { label: "Closed",         color: "bg-gray-200 text-gray-700" },
};

import { fmtUSD } from "../utils/currency";
function fmt$(v) { return fmtUSD(v); }

export default function CapitalMatchingDetail({ leadId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [transitioning, setTransitioning] = useState(false);
  const [transitionReason, setTransitionReason] = useState("");
  const [noteContent, setNoteContent] = useState("");
  const [noteType, setNoteType] = useState("matching");
  const [submittingNote, setSubmittingNote] = useState(false);
  const [lenderInput, setLenderInput] = useState("");
  const [assignNote, setAssignNote] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [activity, setActivity] = useState(null);
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchDetail = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/capital-matching/deal/${leadId}`, { headers })
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [leadId]);

  const fetchActivity = useCallback(() => {
    axios.get(`${API}/capital-matching/deal/${leadId}/activity`, { headers })
      .then(r => setActivity(r.data))
      .catch(console.error);
  }, [leadId]);

  useEffect(() => { fetchDetail(); fetchActivity(); }, [fetchDetail, fetchActivity]);

  const handleTransition = async (toStage) => {
    setTransitioning(true);
    try {
      await axios.post(`${API}/capital-matching/deal/${leadId}/transition`,
        { to_stage: toStage, reason: transitionReason || null }, { headers });
      setTransitionReason("");
      fetchDetail();
    } catch (err) { alert(err.response?.data?.detail || "Transition failed"); }
    finally { setTransitioning(false); }
  };

  const handleAssignLenders = async () => {
    const ids = lenderInput.split(",").map(s => s.trim()).filter(Boolean);
    if (!ids.length) return;
    setAssigning(true);
    try {
      await axios.post(`${API}/capital-matching/deal/${leadId}/assign-lenders`,
        { lender_ids: ids, note: assignNote || null }, { headers });
      setLenderInput("");
      setAssignNote("");
      fetchDetail();
      fetchActivity();
    } catch (err) { alert(err.response?.data?.detail || "Assignment failed"); }
    finally { setAssigning(false); }
  };

  const handleLenderStatusUpdate = async (lenderId, newStatus) => {
    try {
      await axios.patch(`${API}/capital-matching/deal/${leadId}/lender/${lenderId}/status`,
        { status: newStatus }, { headers });
      fetchDetail();
      fetchActivity();
    } catch (err) { alert(err.response?.data?.detail || "Status update failed"); }
  };

  const handleAddNote = async () => {
    if (!noteContent.trim()) return;
    setSubmittingNote(true);
    try {
      await axios.post(`${API}/capital-matching/deal/${leadId}/notes`,
        { content: noteContent, note_type: noteType }, { headers });
      setNoteContent("");
      fetchDetail();
    } catch (err) { alert(err.response?.data?.detail || "Failed to add note"); }
    finally { setSubmittingNote(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-indigo-600" /></div>;
  if (!data) return <div className="text-center py-20 text-sm text-[#888]">Deal not found</div>;

  const { deal, extra, borrower, documents, notes, matching, readiness } = data;
  const stage = deal.stage;
  const stageMeta = STAGE_META[stage] || STAGE_META.approved;
  const transitions = VALID_TRANSITIONS[stage] || [];

  const tabs = [
    { key: "overview", label: "Overview" },
    { key: "lenders", label: `Lenders (${matching.lenders_assigned})` },
    { key: "activity", label: "Activity" },
    { key: "documents", label: `Documents (${documents.length})` },
    { key: "notes", label: `Notes (${notes.length})` },
  ];

  return (
    <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-8" data-testid="capital-matching-detail">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-[#888] hover:text-[#333] mb-4 transition-colors" data-testid="cm-back-btn">
        <ArrowLeft className="h-4 w-4" /> Back to Pipeline
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-xl font-bold text-[#0A0A0A]">{deal.deal_number}</h1>
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${stageMeta.bg} ${stageMeta.text}`} data-testid="cm-stage-badge">
              {stageMeta.label}
            </span>
          </div>
          <p className="text-sm text-[#666]">
            {deal.borrower_name} · {LOAN_LABELS[deal.loan_type] || deal.loan_type} · {deal.property_address || deal.state}
          </p>
        </div>

        {transitions.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap" data-testid="cm-transition-actions">
            <input type="text" placeholder="Reason (optional)" value={transitionReason}
              onChange={e => setTransitionReason(e.target.value)} data-testid="cm-transition-reason"
              className="px-3 py-2 rounded-lg border border-[#e5e5e5] text-sm w-48 focus:outline-none focus:ring-2 focus:ring-indigo-200" />
            {transitions.map(t => (
              <button key={t} onClick={() => handleTransition(t)} disabled={transitioning}
                data-testid={`cm-transition-${t}`}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  t === "funding" ? "bg-emerald-600 text-white hover:bg-emerald-700"
                  : t === "terms" ? "bg-violet-600 text-white hover:bg-violet-700"
                  : "bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100"
                } disabled:opacity-50`}>
                {transitioning ? <Loader2 className="h-4 w-4 animate-spin" /> : (STAGE_META[t]?.label || t.replace("_", " "))}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Matching Readiness Panel */}
      <div className={`rounded-xl border p-4 mb-6 ${readiness.ready_for_terms ? "bg-emerald-50 border-emerald-200" : "bg-indigo-50 border-indigo-200"}`} data-testid="matching-readiness-panel">
        <div className="flex items-center gap-2 mb-3">
          <Handshake className={`h-4 w-4 ${readiness.ready_for_terms ? "text-emerald-600" : "text-indigo-600"}`} />
          <span className="text-sm font-semibold text-[#0A0A0A]">
            Matching Readiness: {readiness.ready_for_terms ? "Ready for Terms" : "In Progress"}
          </span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            ["deal_approved", readiness.deal_approved, "Deal Approved"],
            ["has_lenders_assigned", readiness.has_lenders_assigned, `Lenders (${matching.lenders_assigned})`],
            ["has_interest", readiness.has_interest, `Interest (${matching.interests?.length || 0})`],
            ["has_term_sheets", readiness.has_term_sheets, `Term Sheets (${matching.term_sheets?.length || 0})`],
          ].map(([key, val, label]) => (
            <div key={key} className="flex items-center gap-1.5 text-xs">
              {val ? <CheckCircle className="h-3.5 w-3.5 text-emerald-500" /> : <XCircle className="h-3.5 w-3.5 text-[#ccc]" />}
              <span className={val ? "text-[#444]" : "text-[#999]"}>{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[#eee] mb-6">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`cm-tab-${t.key}`}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              tab === t.key ? "border-indigo-600 text-indigo-700" : "border-transparent text-[#888] hover:text-[#333]"
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={tab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
          {tab === "overview" && <OverviewTab deal={deal} extra={extra} borrower={borrower} />}
          {tab === "lenders" && (
            <LendersTab matching={matching} lenderInput={lenderInput} setLenderInput={setLenderInput}
              assignNote={assignNote} setAssignNote={setAssignNote} onAssign={handleAssignLenders} assigning={assigning}
              onStatusUpdate={handleLenderStatusUpdate} />
          )}
          {tab === "activity" && <ActivityTab activity={activity} />}
          {tab === "documents" && <DocumentsTab documents={documents} />}
          {tab === "notes" && (
            <NotesTab notes={notes} noteContent={noteContent} setNoteContent={setNoteContent}
              noteType={noteType} setNoteType={setNoteType} onSubmit={handleAddNote} submitting={submittingNote} />
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}


/* ── Overview Tab ─────────────────────────────────────────── */
function OverviewTab({ deal, extra, borrower }) {
  const sections = [
    {
      title: "Deal Summary", icon: DollarSign, items: [
        ["Loan Type", LOAN_LABELS[deal.loan_type] || deal.loan_type],
        ["Purchase Price", fmt$(deal.purchase_price)],
        ["Rehab Budget", fmt$(deal.rehab_budget)],
        ["ARV", fmt$(deal.arv)],
        ["Requested Loan", fmt$(deal.requested_loan_amount)],
        ["LTV", deal.purchase_price ? `${((deal.requested_loan_amount / deal.purchase_price) * 100).toFixed(1)}%` : "—"],
        ["LTARV", deal.arv ? `${((deal.requested_loan_amount / deal.arv) * 100).toFixed(1)}%` : "—"],
        ["Exit Strategy", extra?.exit_strategy || "—"],
      ],
    },
    {
      title: "Property", icon: Building2, items: [
        ["Address", deal.property_address || "—"],
        ["Type", extra?.property_type || deal.property_type || "—"],
        ["Beds / Baths", `${extra?.bedrooms || "—"} / ${extra?.bathrooms || "—"}`],
        ["Sqft", extra?.sqft ? `${Number(extra.sqft).toLocaleString()} sqft` : "—"],
        ["Year Built", extra?.year_built || "—"],
      ],
    },
    {
      title: "Borrower", icon: User, items: [
        ["Name", borrower?.full_name || deal.borrower_name || "—"],
        ["Email", borrower?.email || extra?.email || "—"],
        ["Phone", borrower?.phone || extra?.phone || "—"],
        ["Entity", borrower?.entity_name || extra?.entity_name || "—"],
        ["FICO", deal.fico_estimate || extra?.fico_estimate || "—"],
      ],
    },
  ];
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {sections.map(s => (
        <div key={s.title} className="rounded-xl border border-[#eee] bg-white p-4">
          <div className="flex items-center gap-2 mb-3">
            <s.icon className="h-4 w-4 text-indigo-500" />
            <span className="text-sm font-semibold text-[#0A0A0A]">{s.title}</span>
          </div>
          <dl className="space-y-2">
            {s.items.map(([label, value]) => (
              <div key={label} className="flex justify-between text-xs">
                <dt className="text-[#888]">{label}</dt>
                <dd className="text-[#333] font-medium text-right max-w-[60%] truncate">{value}</dd>
              </div>
            ))}
          </dl>
        </div>
      ))}
    </div>
  );
}


/* ── Lenders Tab ──────────────────────────────────────────── */
function LendersTab({ matching, lenderInput, setLenderInput, assignNote, setAssignNote, onAssign, assigning, onStatusUpdate }) {
  return (
    <div className="space-y-4">
      {/* Assign lenders form */}
      <div className="rounded-xl border border-[#eee] bg-white p-4" data-testid="assign-lenders-form">
        <div className="flex items-center gap-2 mb-3">
          <Users className="h-4 w-4 text-indigo-500" />
          <span className="text-sm font-semibold text-[#0A0A0A]">Assign Lenders</span>
        </div>
        <p className="text-xs text-[#888] mb-3">Enter lender user IDs (comma-separated) to associate them with this deal.</p>
        <input type="text" value={lenderInput} onChange={e => setLenderInput(e.target.value)}
          placeholder="lender-user-id-1, lender-user-id-2" data-testid="lender-ids-input"
          className="w-full px-3 py-2.5 rounded-lg border border-[#e5e5e5] text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-indigo-200" />
        <input type="text" value={assignNote} onChange={e => setAssignNote(e.target.value)}
          placeholder="Assignment note (optional)" data-testid="assign-note-input"
          className="w-full px-3 py-2 rounded-lg border border-[#e5e5e5] text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-indigo-200" />
        <button onClick={onAssign} disabled={assigning || !lenderInput.trim()} data-testid="assign-lenders-btn"
          className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition-all flex items-center gap-2">
          {assigning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Users className="h-4 w-4" />}
          Assign
        </button>
      </div>

      {/* Assigned lenders list */}
      {matching.lender_summaries?.length > 0 && (
        <div className="rounded-xl border border-[#eee] bg-white p-4">
          <div className="text-xs uppercase tracking-wider text-[#888] mb-3">Assigned Lenders ({matching.lenders_assigned})</div>
          <div className="space-y-2">
            {matching.lender_summaries.map((l, i) => (
              <div key={l.user_id || i} className="flex items-center gap-3 p-3 rounded-lg border border-[#f0f0f0]" data-testid={`lender-${i}`}>
                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                  <User className="h-4 w-4 text-indigo-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-[#0A0A0A]">{l.full_name}</div>
                  <div className="text-xs text-[#888]">{l.company || l.email}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Lender Interest */}
      {matching.interests?.length > 0 && (
        <div className="rounded-xl border border-[#eee] bg-white p-4" data-testid="lender-interests-panel">
          <div className="flex items-center gap-2 mb-3">
            <Handshake className="h-4 w-4 text-emerald-500" />
            <span className="text-xs uppercase tracking-wider text-[#888]">Lender Interest ({matching.interests.length})</span>
          </div>
          <div className="space-y-2">
            {matching.interests.map((interest, i) => {
              const meta = LENDER_STATUS_META[interest.status] || LENDER_STATUS_META.received;
              const transitions = interest.allowed_transitions || [];
              return (
                <div key={interest.interest_id || i} className="flex items-start gap-3 p-3 rounded-lg border border-emerald-100 bg-emerald-50/30" data-testid={`lender-interest-${i}`}>
                  <Handshake className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-[#0A0A0A]">{interest.lender_name}</div>
                    {interest.lender_company && <div className="text-xs text-[#888]">{interest.lender_company}</div>}
                    {interest.message && <div className="text-xs text-[#666] mt-0.5 italic">{interest.message}</div>}
                    <div className="text-[10px] text-[#aaa] mt-0.5">{interest.created_at?.slice(0, 10)}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${meta.color}`} data-testid={`lender-interest-status-${i}`}>
                      {meta.label}
                    </span>
                    {transitions.length > 0 && (
                      <select
                        data-testid={`lender-status-select-${i}`}
                        defaultValue=""
                        onChange={(e) => { if (e.target.value) { onStatusUpdate(interest.lender_id, e.target.value); e.target.value = ""; } }}
                        className="text-[10px] border border-[#ddd] rounded-md px-1.5 py-1 bg-white text-[#555] cursor-pointer focus:outline-none focus:ring-1 focus:ring-indigo-200"
                      >
                        <option value="" disabled>Move to...</option>
                        {transitions.map(t => (
                          <option key={t} value={t}>{(LENDER_STATUS_META[t] || {}).label || t}</option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Term Sheets */}
      {matching.term_sheets?.length > 0 && (
        <div className="rounded-xl border border-[#eee] bg-white p-4" data-testid="term-sheets-panel">
          <div className="flex items-center gap-2 mb-3">
            <FileText className="h-4 w-4 text-violet-500" />
            <span className="text-xs uppercase tracking-wider text-[#888]">Term Sheets ({matching.term_sheets.length})</span>
          </div>
          <div className="space-y-2">
            {matching.term_sheets.map((ts, i) => (
              <div key={ts.term_sheet_id || i} className="flex items-start gap-3 p-3 rounded-lg border border-violet-100 bg-violet-50/30" data-testid={`term-sheet-${i}`}>
                <FileText className="h-5 w-5 text-violet-500 shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-[#0A0A0A]">{ts.lender_name || ts.lender_id}</div>
                  {ts.lender_company && <div className="text-xs text-[#888]">{ts.lender_company}</div>}
                  <div className="flex flex-wrap gap-3 mt-1.5">
                    {ts.rate != null && <span className="text-xs text-[#555]">Rate: <b>{ts.rate}%</b></span>}
                    {ts.ltv != null && <span className="text-xs text-[#555]">LTV: <b>{ts.ltv}%</b></span>}
                    {ts.term_months != null && <span className="text-xs text-[#555]">Term: <b>{ts.term_months}mo</b></span>}
                  </div>
                  {ts.notes && <div className="text-xs text-[#666] mt-1 italic">{ts.notes}</div>}
                  <div className="text-[10px] text-[#aaa] mt-0.5">{ts.created_at?.slice(0, 10)}</div>
                </div>
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full shrink-0 ${
                  ts.status === "accepted" ? "bg-emerald-100 text-emerald-700" :
                  ts.status === "withdrawn" ? "bg-red-50 text-red-600" :
                  "bg-violet-100 text-violet-600"
                }`}>{ts.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Outreach Timeline */}
      {matching.outreach?.length > 0 && (
        <div className="rounded-xl border border-[#eee] bg-white p-4" data-testid="outreach-timeline-panel">
          <div className="flex items-center gap-2 mb-3">
            <Send className="h-4 w-4 text-blue-500" />
            <span className="text-xs uppercase tracking-wider text-[#888]">Outreach ({matching.outreach.length})</span>
          </div>
          <div className="space-y-2">
            {matching.outreach.map((o, i) => (
              <div key={o.outreach_id || i} className="flex items-start gap-3 p-3 rounded-lg border border-blue-100 bg-blue-50/30" data-testid={`outreach-${i}`}>
                <Send className="h-5 w-5 text-blue-500 shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-[#0A0A0A]">To: {o.lender_name}</div>
                  {o.email_subject && <div className="text-xs text-[#555] mt-0.5">{o.email_subject}</div>}
                  {o.sender_name && <div className="text-xs text-[#888] mt-0.5">Sent by {o.sender_name}</div>}
                  <div className="text-[10px] text-[#aaa] mt-0.5">{o.sent_at?.slice(0, 10) || o.created_at?.slice(0, 10)}</div>
                </div>
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full shrink-0 ${
                  o.response_status === "replied" ? "bg-emerald-100 text-emerald-700" :
                  o.response_status === "bounced" ? "bg-red-50 text-red-600" :
                  "bg-blue-100 text-blue-600"
                }`}>{o.response_status || "sent"}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {matching.lender_summaries?.length === 0 && matching.term_sheets?.length === 0 &&
       matching.interests?.length === 0 && matching.outreach?.length === 0 && (
        <div className="text-center py-8">
          <Users className="h-8 w-8 text-[#ccc] mx-auto mb-2" />
          <p className="text-sm text-[#888]">No lenders assigned yet</p>
        </div>
      )}
    </div>
  );
}


/* ── Activity Tab ─────────────────────────────────────────── */
function ActivityTab({ activity }) {
  if (!activity) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#ccc]" /></div>;
  const { timeline, totals } = activity;
  return (
    <div className="space-y-4">
      <div className="flex gap-4 mb-4 flex-wrap">
        {Object.entries(totals).map(([k, v]) => (
          <div key={k} className="rounded-lg border border-[#eee] bg-white px-4 py-3 text-center">
            <div className="text-lg font-bold text-[#0A0A0A]">{v}</div>
            <div className="text-[10px] uppercase text-[#888] tracking-wider">{k.replace("_", " ")}</div>
          </div>
        ))}
      </div>
      {timeline.length === 0 ? (
        <div className="text-center py-8"><p className="text-sm text-[#888]">No matching activity yet</p></div>
      ) : (
        <div className="space-y-2" data-testid="cm-activity-timeline">
          {timeline.map((ev, i) => (
            <div key={i} className="flex items-start gap-3 p-3 rounded-xl border border-[#eee] bg-white">
              <div className={`mt-0.5 w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                ev.type === "assignment" ? "bg-indigo-100" :
                ev.type === "interest" ? "bg-emerald-100" :
                ev.type === "outreach" ? "bg-blue-100" :
                "bg-violet-100"
              }`}>
                {ev.type === "assignment" ? <Users className="h-3 w-3 text-indigo-600" /> :
                 ev.type === "interest" ? <Handshake className="h-3 w-3 text-emerald-600" /> :
                 ev.type === "outreach" ? <Send className="h-3 w-3 text-blue-600" /> :
                 <FileText className="h-3 w-3 text-violet-600" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-[#0A0A0A]">
                  {ev.type === "assignment" ? `${ev.assigned_by_name || "Unknown"} assigned ${ev.lender_ids?.length || 0} lender(s)` :
                   ev.type === "interest" ? `${ev.lender_name || "Lender"} expressed interest` :
                   ev.type === "outreach" ? `Outreach sent to ${ev.lender_name || "lender"}` :
                   `Term sheet submitted by ${ev.lender_name || "lender"}`}
                </div>
                {(ev.note || ev.message || ev.email_subject) &&
                  <p className="text-xs text-[#888] mt-0.5">{ev.note || ev.message || ev.email_subject}</p>}
                <span className="text-[10px] text-[#aaa]">{ev.at?.slice(0, 16).replace("T", " ")}</span>
              </div>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                ev.type === "assignment" ? "bg-indigo-50 text-indigo-600" :
                ev.type === "interest" ? "bg-emerald-50 text-emerald-600" :
                ev.type === "outreach" ? "bg-blue-50 text-blue-600" :
                "bg-violet-50 text-violet-600"
              }`}>{ev.type}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/* ── Documents Tab ────────────────────────────────────────── */
function DocumentsTab({ documents }) {
  if (!documents.length) return (
    <div className="text-center py-12">
      <FileText className="h-8 w-8 text-[#ccc] mx-auto mb-2" />
      <p className="text-sm text-[#888]">No documents uploaded</p>
    </div>
  );
  return (
    <div className="space-y-2" data-testid="cm-documents-list">
      {documents.map((doc, i) => (
        <div key={doc.doc_id || i} className="flex items-center gap-3 rounded-xl border border-[#eee] bg-white p-3">
          <FileText className="h-5 w-5 text-indigo-400 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-sm text-[#0A0A0A] truncate">{doc.filename}</div>
            <div className="text-xs text-[#888]">{doc.category_label || doc.category}</div>
          </div>
          <span className="text-[10px] text-[#aaa]">{doc.created_at?.slice(0, 10)}</span>
        </div>
      ))}
    </div>
  );
}


/* ── Notes Tab ────────────────────────────────────────────── */
function NotesTab({ notes, noteContent, setNoteContent, noteType, setNoteType, onSubmit, submitting }) {
  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-[#eee] bg-white p-4" data-testid="cm-add-note-form">
        <div className="flex items-center gap-2 mb-3">
          <MessageSquare className="h-4 w-4 text-indigo-500" />
          <span className="text-sm font-semibold text-[#0A0A0A]">Add Note</span>
        </div>
        <div className="flex gap-2 mb-3">
          {NOTE_TYPES.map(t => (
            <button key={t.value} onClick={() => setNoteType(t.value)} data-testid={`cm-note-type-${t.value}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                noteType === t.value ? "bg-indigo-100 text-indigo-700 border border-indigo-200" : "bg-[#f8f8f8] text-[#888] border border-transparent hover:border-[#ddd]"
              }`}>{t.label}</button>
          ))}
        </div>
        <textarea value={noteContent} onChange={e => setNoteContent(e.target.value)}
          placeholder="Write your matching note..." data-testid="cm-note-content"
          className="w-full p-3 rounded-lg border border-[#e5e5e5] text-sm resize-none h-24 focus:outline-none focus:ring-2 focus:ring-indigo-200" />
        <button onClick={onSubmit} disabled={submitting || !noteContent.trim()} data-testid="cm-submit-note-btn"
          className="mt-2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition-all flex items-center gap-2">
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          Add Note
        </button>
      </div>
      <div className="space-y-2">
        {notes.map((n, i) => (
          <div key={n.note_id || i} className="rounded-xl border border-[#eee] bg-white p-4" data-testid={`cm-note-${i}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-semibold text-[#444]">{n.author_name}</span>
              <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${
                n.note_type === "matching" ? "bg-indigo-50 text-indigo-600" :
                n.note_type === "outreach" ? "bg-blue-50 text-blue-600" :
                n.note_type === "negotiation" ? "bg-amber-50 text-amber-600" :
                "bg-[#f5f5f5] text-[#888]"
              }`}>{n.note_type}</span>
              <span className="text-[10px] text-[#aaa] ml-auto">{n.created_at?.slice(0, 16).replace("T", " ")}</span>
            </div>
            <p className="text-sm text-[#333] whitespace-pre-wrap">{n.content}</p>
          </div>
        ))}
        {!notes.length && <p className="text-sm text-[#888] text-center py-6">No notes yet</p>}
      </div>
    </div>
  );
}
